import React, { useState, useEffect } from "react";
import Markdown from "react-markdown";

// MUI
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import {
  Check as CheckIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  ContentCopyOutlined as CopyIcon,
  FiberManualRecord as DotIcon,
  LightbulbOutlined as LightbulbIcon,
  Menu as MenuIcon,
  NoteAddOutlined as NoteAddOutlinedIcon,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
  Send as SendIcon,
} from "@mui/icons-material";

// Sequence
import {
  ACTIONS,
  HistoryItem,
  MAIN_CARD_NAME,
  MeScriptSequenceType,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
} from "./sequence/MeScript";

// Common
import SnackMessage from "../common/SnackMessage";
import { copyToClipboard } from "react-lib/apps/HISV3/common/CommonInterface";

// Utils
import { formatDate } from "react-lib/utils/dateUtils";

// ============================
// Props
// ============================
type CardMeScriptMainProps = {
  runSequence: RunSequence;
  setProp: SetProp;
  MeScriptSequence?: MeScriptSequenceType;
  errorMessage?: any;
  successMessage?: any;
};

/** Extract text content from think/article version string (python dict format) */
const parseVersionText = (raw: string): string => {
  if (!raw) return "";
  try {
    const match = raw.match(/'text':\s*'([\s\S]*?)(?:',\s*'extras'|'$|\}$)/);
    if (match?.[1]) {
      return match[1].replace(/\\n/g, "\n").replace(/\\t/g, "\t");
    }
    return raw;
  } catch {
    return raw;
  }
};

// ============================
// Styled Components (MeGen theme)
// ============================

// -- Layout --
const RootContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
  backgroundColor: "#ffffff",
  overflow: "hidden",
  position: "relative",
});

const MainLayout = styled(Box)({
  display: "flex",
  flex: 1,
  overflow: "hidden",
});

// -- Sidebar --
const SIDEBAR_WIDTH = 280;

const SidebarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})<{ isCollapsed?: boolean }>(({ isCollapsed }) => ({
  position: "relative",
  display: "flex",
  flexShrink: 0,
  width: isCollapsed ? 30 : "auto",
  transition: "width 0.3s ease",
}));

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})<{ isCollapsed?: boolean }>(({ isCollapsed }) => ({
  width: isCollapsed ? 0 : SIDEBAR_WIDTH,
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  borderRight: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  height: "100%",
  overflow: "hidden",
  transition: "width 0.3s ease",
}));

const SidebarHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px",
});

const ExpandButton = styled(IconButton)({
  position: "absolute",
  left: 8,
  top: 8,
  backgroundColor: "#FFFFFF",
  zIndex: 1005,
  borderRadius: 6,
  padding: 8,
  "&:hover": {
    backgroundColor: "#F5F5F5",
  },
});

const NewScriptButton = styled(Button)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "4px",
  padding: "10px 14px",
  margin: "8px 0px",
  backgroundColor: "transparent",
  color: "#333",
  fontSize: "14px",
  fontWeight: 500,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#EEEEEE",
  },
});

const SearchWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  margin: "0 8px 8px 8px",
  padding: "4px 10px",
  borderRadius: "8px",
  backgroundColor: "#F5F5F5",
  border: "1px solid transparent",
  "&:focus-within": {
    border: "1px solid #E91E63",
    backgroundColor: "#fff",
  },
});

const SearchInput = styled(InputBase)({
  flex: 1,
  fontSize: "13px",
  color: "#333",
  "& input::placeholder": {
    color: "#AAA",
    fontSize: "13px",
  },
});

const IdeaListContainer = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "0 8px",
});

const IdeaCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ isActive }) => ({
  padding: "8px 12px",
  marginBottom: "4px",
  borderRadius: "0.5rem",
  cursor: "pointer",
  backgroundColor: isActive ? "#FFE4E8" : "transparent",
  color: isActive ? "#E91E63" : "#333",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: isActive ? "#FFE4E8" : "#F0F0F0",
  },
}));

const IdeaTitle = styled(Typography)({
  fontSize: "0.8rem",
  fontWeight: 500,
  color: "inherit",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  lineHeight: 1.4,
});

const IdeaDate = styled(Typography)({
  fontSize: "0.7rem",
  color: "#94a3b8",
  marginTop: "4px",
});

const VersionBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "variant",
})<{ variant?: "raw" | "article" }>(({ variant }) => ({
  fontSize: "0.625rem",
  fontWeight: 600,
  padding: "1px 6px",
  borderRadius: "4px",
  backgroundColor: variant === "raw" ? "#dcfce7" : "#dbeafe",
  color: variant === "raw" ? "#16a34a" : "#2563eb",
}));

// -- Content Area --
const ContentArea = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  padding: "24px",
});

const ContentHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "8px",
});

const ContentSubtitle = styled(Typography)({
  fontSize: "0.875rem",
  color: "#64748b",
  marginBottom: "24px",
});

const IdeaInputLabel = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#1e293b",
  marginBottom: "8px",
});

const IdeaTextArea = styled("textarea")({
  width: "100%",
  minHeight: "120px",
  padding: "12px 14px",
  fontSize: "0.875rem",
  fontFamily: "inherit",
  color: "#1e293b",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "0.5rem",
  resize: "vertical",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s ease",
  "&:focus": {
    borderColor: "#e91e63",
  },
  "&::placeholder": {
    color: "#94a3b8",
  },
});

const GenerateButton = styled(Button)({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "10px 24px",
  backgroundColor: "#e91e63",
  color: "#ffffff",
  fontSize: "0.875rem",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "9999px",
  alignSelf: "flex-end",
  marginTop: "12px",
  "&:hover": {
    backgroundColor: "#c2185b",
  },
  "&:disabled": {
    backgroundColor: "#f8bbd9",
    color: "#ffffff",
  },
});

// -- Panels --
const PanelsRow = styled(Box)({
  display: "flex",
  gap: "16px",
  marginTop: "24px",
  flex: 1,
  minHeight: 0,
});

const Panel = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#ffffff",
  borderRadius: "0.75rem",
  border: "1px solid #e2e8f0",
  overflow: "hidden",
});

const PanelHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  padding: "12px 16px",
  borderBottom: "1px solid #e2e8f0",
});

const PanelContent = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "16px",
  fontSize: "0.85rem",
  color: "#334155",
  lineHeight: 1.7,
  whiteSpace: "pre-wrap",
});

const RefineInputWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  borderTop: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
});

const RefineInput = styled(InputBase)({
  flex: 1,
  fontSize: "0.8rem",
  color: "#1e293b",
  "& input::placeholder": {
    color: "#94a3b8",
    fontSize: "0.8rem",
  },
});

const SendButton = styled(IconButton)({
  padding: 6,
  color: "#e91e63",
  "&:hover": {
    backgroundColor: "#fce4ec",
  },
  "&:disabled": {
    color: "#f8bbd9",
  },
});

const VersionNav = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "2px",
});

const VersionNavButton = styled(IconButton)({
  padding: 2,
  color: "#94a3b8",
  "&:hover": {
    color: "#e91e63",
    backgroundColor: "#fce4ec",
  },
  "&:disabled": {
    color: "#e2e8f0",
  },
});

const VersionLabel = styled(Typography)({
  fontSize: "0.7rem",
  fontWeight: 600,
  color: "#64748b",
  minWidth: "40px",
  textAlign: "center",
});

// ============================
// Component
// ============================
const CardMeScriptMain = (props: CardMeScriptMainProps) => {
  // -- State --
  const [ideaText, setIdeaText] = useState("");
  const [refineThinkText, setRefineThinkText] = useState("");
  const [refineArticleText, setRefineArticleText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedPanel, setCopiedPanel] = useState<"think" | "article" | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // -- Derived values --
  const filteredHistory = (props.MeScriptSequence?.historyList || []).filter((item: HistoryItem) =>
    item.idea.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const thinkVersions = props.MeScriptSequence?.ideaState?.think_versions ?? [];
  const articleVersions = props.MeScriptSequence?.ideaState?.article_versions ?? [];
  const hasContent = thinkVersions.length > 0;

  const thinkIdx = props.MeScriptSequence?.currentThinkIdx ?? -1;
  const articleIdx = props.MeScriptSequence?.currentArticleIdx ?? -1;

  const currentThinkText =
    thinkIdx >= 0 && thinkIdx < thinkVersions.length
      ? parseVersionText(thinkVersions[thinkIdx])
      : "";

  const currentArticleText =
    articleIdx >= 0 && articleIdx < articleVersions.length
      ? parseVersionText(articleVersions[articleIdx])
      : "";

  const isBusy =
    props.MeScriptSequence?.isGenerating ||
    props.MeScriptSequence?.isRefiningThink ||
    props.MeScriptSequence?.isRefiningArticle;
  const buttonLabel = hasContent ? "Regenerate Script" : "Generate Script";

  // -- Effects --
  useEffect(() => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      restart: true,
    });

    return () => {
      props.runSequence({
        sequence: SEQUENCE_NAME,
        clear: true,
      });
    };
  }, []);

  useEffect(() => {
    if (props.MeScriptSequence?.ideaState?.idea !== undefined) {
      setIdeaText(props.MeScriptSequence.ideaState.idea);
    }
  }, [props.MeScriptSequence?.ideaState?.idea]);

  // -- Handlers --
  const handleCopy = async (text: string, panel: "think" | "article") => {
    if (!text) return;
    await copyToClipboard(text);
    setCopiedPanel(panel);
    setTimeout(() => setCopiedPanel(null), 2000);
  };

  const handleSelectIdea = (threadId: string) => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD_IDEA,
      threadId,
    });
  };

  const handleNewScript = () => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.NEW_SCRIPT,
    });
    setIdeaText("");
    setRefineThinkText("");
    setRefineArticleText("");
  };

  const handleGenerate = () => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.GENERATE,
      idea: ideaText,
    });
    setRefineThinkText("");
    setRefineArticleText("");
  };

  const handleRefineThink = () => {
    if (!refineThinkText.trim() || !props.MeScriptSequence?.selectedThreadId) return;

    const currentText =
      props.MeScriptSequence?.ideaState?.think_versions?.[
        props.MeScriptSequence.currentThinkIdx ?? -1
      ] || "";

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.REFINE_THINK,
      threadId: props.MeScriptSequence.selectedThreadId,
      currentText,
      feedback: refineThinkText,
    });
    setRefineThinkText("");
  };

  const handleRefineArticle = () => {
    if (!refineArticleText.trim() || !props.MeScriptSequence?.selectedThreadId) return;

    const currentArticle =
      props.MeScriptSequence?.ideaState?.article_versions?.[
        props.MeScriptSequence.currentArticleIdx ?? -1
      ] || "";
    const deepThinkText =
      props.MeScriptSequence?.ideaState?.think_versions?.[
        props.MeScriptSequence.currentThinkIdx ?? -1
      ] || "";

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.REFINE_ARTICLE,
      threadId: props.MeScriptSequence.selectedThreadId,
      currentArticle,
      deepThinkText,
      feedback: refineArticleText,
    });
    setRefineArticleText("");
  };

  const handleClearMessage = () => {
    props.setProp(`errorMessage.${MAIN_CARD_NAME}`, null);
    props.setProp(`successMessage.${MAIN_CARD_NAME}`, null);
  };

  // -- Version navigation --
  const handleThinkPrev = () => {
    const idx = props.MeScriptSequence?.currentThinkIdx ?? -1;
    if (idx > 0) {
      props.setProp("MeScriptSequence.currentThinkIdx", idx - 1);
    }
  };

  const handleThinkNext = () => {
    const idx = props.MeScriptSequence?.currentThinkIdx ?? -1;
    const max = (props.MeScriptSequence?.ideaState?.think_versions?.length ?? 0) - 1;
    if (idx < max) {
      props.setProp("MeScriptSequence.currentThinkIdx", idx + 1);
    }
  };

  const handleArticlePrev = () => {
    const idx = props.MeScriptSequence?.currentArticleIdx ?? -1;
    if (idx > 0) {
      props.setProp("MeScriptSequence.currentArticleIdx", idx - 1);
    }
  };

  const handleArticleNext = () => {
    const idx = props.MeScriptSequence?.currentArticleIdx ?? -1;
    const max = (props.MeScriptSequence?.ideaState?.article_versions?.length ?? 0) - 1;
    if (idx < max) {
      props.setProp("MeScriptSequence.currentArticleIdx", idx + 1);
    }
  };

  return (
    <RootContainer>
      {/* Error & Success Message */}
      <SnackMessage
        error={props.errorMessage?.[MAIN_CARD_NAME]}
        success={props.successMessage?.[MAIN_CARD_NAME]}
        onClose={handleClearMessage}
      />

      <MainLayout>
        {/* ========== Sidebar ========== */}
        <SidebarWrapper isCollapsed={isSidebarCollapsed}>
          {isSidebarCollapsed && (
            <Tooltip title="Open sidebar" placement="right">
              <ExpandButton size="small" onClick={() => setIsSidebarCollapsed(false)}>
                <MenuIcon sx={{ color: "#666", fontSize: 18 }} />
              </ExpandButton>
            </Tooltip>
          )}
          <SidebarContainer isCollapsed={isSidebarCollapsed}>
            <SidebarHeader>
              <Box />
              <Tooltip title="Close sidebar" placement="right">
                <IconButton
                  size="small"
                  onClick={() => setIsSidebarCollapsed(true)}
                  sx={{
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "#EEEEEE" },
                  }}
                >
                  <MenuIcon sx={{ color: "#666", fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </SidebarHeader>

            <NewScriptButton
              fullWidth
              startIcon={<NoteAddOutlinedIcon />}
              onClick={handleNewScript}
            >
              New Script
            </NewScriptButton>

            <SearchWrapper>
              <SearchIcon sx={{ fontSize: 16, color: "#AAA", mr: 0.5 }} />
              <SearchInput
                placeholder="Search scripts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery("")}
                  sx={{ padding: 0, color: "#AAA" }}
                >
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </SearchWrapper>

            <IdeaListContainer>
              {filteredHistory.map((item: HistoryItem) => (
                <IdeaCard
                  key={item.thread_id}
                  isActive={props.MeScriptSequence?.selectedThreadId === item.thread_id}
                  onClick={() => handleSelectIdea(item.thread_id)}
                >
                  <IdeaTitle>{item.idea}</IdeaTitle>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: 0.5,
                    }}
                  >
                    <IdeaDate>{formatDate(new Date(item.updated_at))}</IdeaDate>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <VersionBadge variant="raw">v{item.think_count}</VersionBadge>
                      <VersionBadge variant="article">v{item.article_count}</VersionBadge>
                    </Box>
                  </Box>
                </IdeaCard>
              ))}

              {filteredHistory.length === 0 && !props.MeScriptSequence?.isLoading && (
                <Typography
                  sx={{
                    textAlign: "center",
                    color: "#94a3b8",
                    fontSize: "0.8rem",
                    mt: 3,
                  }}
                >
                  {searchQuery ? "No matching scripts" : "No ideas yet"}
                </Typography>
              )}
            </IdeaListContainer>
          </SidebarContainer>
        </SidebarWrapper>

        {/* ========== Main Content ========== */}
        <ContentArea>
          {/* Header */}
          <ContentHeader>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "#fce4ec",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LightbulbIcon sx={{ fontSize: 20, color: "#e91e63" }} />
            </Box>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#1e293b" }}>
              Idea to Script
            </Typography>
          </ContentHeader>

          <ContentSubtitle>
            Turn your rough ideas or long texts into deeply researched, engaging 6-10 minute
            articles.
          </ContentSubtitle>

          {/* Idea Input */}
          <IdeaInputLabel>Your Idea or Topic</IdeaInputLabel>
          <IdeaTextArea
            placeholder="Enter your idea, topic, or paste a long text here..."
            value={ideaText}
            onChange={(e) => setIdeaText(e.target.value)}
            disabled={isBusy}
          />

          {/* Generate / Regenerate Button */}
          <GenerateButton
            startIcon={
              props.MeScriptSequence?.isGenerating ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <PlayArrowIcon />
              )
            }
            onClick={handleGenerate}
            disabled={isBusy || !ideaText?.trim()}
          >
            {props.MeScriptSequence?.isGenerating ? "Generating..." : buttonLabel}
          </GenerateButton>

          {/* Panels Row */}
          <PanelsRow>
            {/* ---- Raw Deep Thinking Panel ---- */}
            <Panel>
              <PanelHeader>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DotIcon sx={{ fontSize: 10, color: "#e91e63" }} />
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 700,
                      color: "#1e293b",
                    }}
                  >
                    Raw Deep Thinking
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {thinkVersions.length > 1 && (
                    <VersionNav>
                      <VersionNavButton
                        size="small"
                        onClick={handleThinkPrev}
                        disabled={thinkIdx <= 0}
                      >
                        <ChevronLeftIcon sx={{ fontSize: 16 }} />
                      </VersionNavButton>
                      <VersionLabel>
                        {thinkIdx + 1}/{thinkVersions.length}
                      </VersionLabel>
                      <VersionNavButton
                        size="small"
                        onClick={handleThinkNext}
                        disabled={thinkIdx >= thinkVersions.length - 1}
                      >
                        <ChevronRightIcon sx={{ fontSize: 16 }} />
                      </VersionNavButton>
                    </VersionNav>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(currentThinkText, "think")}
                    disabled={!currentThinkText}
                    sx={{
                      padding: "4px",
                      color: copiedPanel === "think" ? "#16a34a" : "#94a3b8",
                      "&:hover": { backgroundColor: "#fce4ec", color: "#e91e63" },
                    }}
                  >
                    {copiedPanel === "think" ? (
                      <CheckIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <CopyIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Box>
              </PanelHeader>

              <PanelContent>
                {props.MeScriptSequence?.isLoading || props.MeScriptSequence?.isGenerating ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "#94a3b8",
                    }}
                  >
                    <CircularProgress size={14} sx={{ color: "#e91e63" }} />
                    <span>
                      {props.MeScriptSequence?.isGenerating ? "Generating..." : "Loading..."}
                    </span>
                  </Box>
                ) : currentThinkText ? (
                  currentThinkText
                ) : (
                  <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>
                    Generate a script to see deep thinking results here.
                  </Typography>
                )}
              </PanelContent>

              <RefineInputWrapper>
                <RefineInput
                  placeholder="Refine deep thinking... e.g. Add more facts"
                  value={refineThinkText}
                  onChange={(e) => setRefineThinkText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleRefineThink()}
                  disabled={
                    !hasContent ||
                    props.MeScriptSequence?.isRefiningThink ||
                    props.MeScriptSequence?.isGenerating
                  }
                />
                <SendButton
                  size="small"
                  onClick={handleRefineThink}
                  disabled={
                    !hasContent ||
                    !refineThinkText.trim() ||
                    props.MeScriptSequence?.isRefiningThink ||
                    props.MeScriptSequence?.isGenerating
                  }
                >
                  {props.MeScriptSequence?.isRefiningThink ? (
                    <CircularProgress size={14} sx={{ color: "#e91e63" }} />
                  ) : (
                    <SendIcon sx={{ fontSize: 18 }} />
                  )}
                </SendButton>
              </RefineInputWrapper>
            </Panel>

            {/* ---- Final Article Panel ---- */}
            <Panel>
              <PanelHeader>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DotIcon sx={{ fontSize: 10, color: "#e91e63" }} />
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 700,
                      color: "#1e293b",
                    }}
                  >
                    Final Article
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {articleVersions.length > 1 && (
                    <VersionNav>
                      <VersionNavButton
                        size="small"
                        onClick={handleArticlePrev}
                        disabled={articleIdx <= 0}
                      >
                        <ChevronLeftIcon sx={{ fontSize: 16 }} />
                      </VersionNavButton>
                      <VersionLabel>
                        {articleIdx + 1}/{articleVersions.length}
                      </VersionLabel>
                      <VersionNavButton
                        size="small"
                        onClick={handleArticleNext}
                        disabled={articleIdx >= articleVersions.length - 1}
                      >
                        <ChevronRightIcon sx={{ fontSize: 16 }} />
                      </VersionNavButton>
                    </VersionNav>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(currentArticleText, "article")}
                    disabled={!currentArticleText}
                    sx={{
                      padding: "4px",
                      color: copiedPanel === "article" ? "#16a34a" : "#94a3b8",
                      "&:hover": { backgroundColor: "#fce4ec", color: "#e91e63" },
                    }}
                  >
                    {copiedPanel === "article" ? (
                      <CheckIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <CopyIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Box>
              </PanelHeader>

              <PanelContent>
                {props.MeScriptSequence?.isLoading || props.MeScriptSequence?.isGenerating ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "#94a3b8",
                    }}
                  >
                    <CircularProgress size={14} sx={{ color: "#e91e63" }} />
                    <span>
                      {props.MeScriptSequence?.isGenerating ? "Generating..." : "Loading..."}
                    </span>
                  </Box>
                ) : currentArticleText ? (
                  <Markdown>{currentArticleText}</Markdown>
                ) : (
                  <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>
                    Generate a script to see the final article here.
                  </Typography>
                )}
              </PanelContent>

              <RefineInputWrapper>
                <RefineInput
                  placeholder="Refine article... e.g. Make it funnier"
                  value={refineArticleText}
                  onChange={(e) => setRefineArticleText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleRefineArticle()}
                  disabled={
                    !hasContent ||
                    props.MeScriptSequence?.isRefiningArticle ||
                    props.MeScriptSequence?.isGenerating
                  }
                />
                <SendButton
                  size="small"
                  onClick={handleRefineArticle}
                  disabled={
                    !hasContent ||
                    !refineArticleText.trim() ||
                    props.MeScriptSequence?.isRefiningArticle ||
                    props.MeScriptSequence?.isGenerating
                  }
                >
                  {props.MeScriptSequence?.isRefiningArticle ? (
                    <CircularProgress size={14} sx={{ color: "#e91e63" }} />
                  ) : (
                    <SendIcon sx={{ fontSize: 18 }} />
                  )}
                </SendButton>
              </RefineInputWrapper>
            </Panel>
          </PanelsRow>
        </ContentArea>
      </MainLayout>
    </RootContainer>
  );
};

export default CardMeScriptMain;
